import { useMemo, useState } from "react";
import { Input } from "./ui/input";

const sar = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }));
const healthTone = (h) => (h >= 80 ? "bg-emerald-100 text-emerald-700" : h >= 55 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700");

const Row = ({ r, i }) => (
  <tr className="border-b border-slate-50 align-top hover:bg-slate-50/60" data-testid={`fr-row-${i}`}>
    <td className="p-2"><p className="font-semibold text-slate-800">{r.name || r.rider_id}</p><p className="text-[10px] text-slate-400"><span dir="ltr">{r.rider_id}</span> · {r.platform}</p></td>
    <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${healthTone(r.health)}`} data-testid={`fr-health-${i}`}>{r.health}</span></td>
    <td className="p-2 tabular-nums">{r.orders}</td>
    <td className="p-2 tabular-nums">{r.days}</td>
    <td className="p-2 tabular-nums">{r.daily_avg}{r.prev_daily_avg != null && <span className={`block text-[10px] ${r.daily_avg >= r.prev_daily_avg ? "text-emerald-600" : "text-rose-600"}`}>الماضي {r.prev_daily_avg}</span>}</td>
    <td className="p-2 tabular-nums">{r.acceptance != null ? `${r.acceptance}%` : "—"}</td>
    <td className="p-2 tabular-nums">{r.cancel_pct}%</td>
    <td className="p-2 tabular-nums">{r.no_shows}</td>
    <td className="p-2 tabular-nums">{r.lost_hours_pct}%</td>
    <td className="p-2 tabular-nums">{sar(r.revenue_to_date)}</td>
    <td className="p-2 tabular-nums font-semibold text-blue-700">{sar(r.projected)}</td>
    <td className="p-2 tabular-nums text-rose-600">{r.lost_revenue ? sar(r.lost_revenue) : "—"}</td>
    <td className="p-2"><div className="flex max-w-xs flex-wrap gap-1">{r.flags.map((f) => <span key={f} className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] text-rose-700">{f}</span>)}</div></td>
  </tr>
);

export const ForecastRiders = ({ riders }) => {
  const [plat, setPlat] = useState("all");
  const [onlyFlags, setOnlyFlags] = useState(true);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(100);
  const plats = useMemo(() => Array.from(new Set(riders.map((r) => r.platform))), [riders]);
  const rows = riders.filter((r) => (plat === "all" || r.platform === plat) && (!onlyFlags || r.flags.length) && (!q || `${r.name} ${r.rider_id}`.toLowerCase().includes(q.toLowerCase())));
  const count = (k) => rows.filter((r) => r.flags.some((f) => f.startsWith(k))).length;
  return (
    <div className="space-y-3" data-testid="forecast-riders">
      <div className="flex flex-wrap gap-2 text-xs">
        {[["غياب", "غياب"], ["قبول ضعيف", "قبول ضعيف"], ["إلغاءات", "إلغاءات عالية"], ["ساعات", "ساعات ضائعة"], ["تراجع", "تراجع"], ["تحت التعادل", "تحت التعادل"]].map(([k, l]) => (
          <span key={k} className="rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700" data-testid={`fr-count-${k}`}>{l}: {count(k)}</span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={plat} onChange={(e) => setPlat(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="fr-platform">
          <option value="all">كل المنصات</option>{plats.map((p) => <option key={p}>{p}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={onlyFlags} onChange={(e) => setOnlyFlags(e.target.checked)} data-testid="fr-only-flags" /> اللي فيهم خلل فقط</label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو المعرّف" className="h-9 w-56" data-testid="fr-search" />
        <span className="text-xs text-slate-500">{rows.length} مندوب · الأضعف أولاً</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-right text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>{["المندوب", "الصحة", "الطلبات", "أيام العمل", "المتوسط اليومي", "القبول", "الإلغاء", "عدم حضور", "ساعات ضائعة", "الإيراد لين الآن", "المتوقع", "الضائع", "وين الخلل"].map((h) => <th key={h} className="whitespace-nowrap p-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{rows.slice(0, limit).map((r, i) => <Row key={`${r.platform}-${r.rider_id}`} r={r} i={i} />)}</tbody>
        </table>
        {!rows.length && <p className="p-8 text-center text-sm text-slate-400" data-testid="fr-empty">لا يوجد مناديب مطابقين (يلزم رفع تقارير يومية للشهر)</p>}
      </div>
      {rows.length > limit && <button className="text-sm text-blue-600 underline" onClick={() => setLimit((l) => l + 100)} data-testid="fr-more">عرض المزيد</button>}
    </div>
  );
};
